/**
 * Web forms builder v1 + embed — T-034 (docs/10-design/09 §3–4 · tasks/W3-CONTRACTS.md §10)
 * self-contained ยกเว้น `Condition`/`Field`/`Op` ที่ import จาก ./workflow.ts (ของ T-030 — reuse ตามสัญญา §10)
 */
import { z } from "zod";
import { Condition } from "./workflow.ts";

const I18n = z.record(z.string(), z.string());

export const FormVisibility = z.enum(["public", "authenticated", "internal"]);
export type FormVisibility = z.infer<typeof FormVisibility>;

export const FormStatus = z.enum(["draft", "published", "archived"]);
export type FormStatus = z.infer<typeof FormStatus>;

export const FormFieldType = z.enum([
  "text",
  "textarea",
  "richtext",
  "email",
  "phone",
  "number",
  "date",
  "select",
  "multiselect",
  "radio",
  "checkbox",
  "file",
  "hidden",
  "section",
  "consent",
  "ticket_ref",
]);
export type FormFieldType = z.infer<typeof FormFieldType>;

export const FormFieldKey = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,63}$/, "key ต้องขึ้นต้นด้วย a-z ตามด้วย a-z0-9_ ความยาวไม่เกิน 64 ตัว");

export const FormFieldOption = z.strictObject({
  value: z.string().min(1).max(200),
  label: I18n,
});
export type FormFieldOption = z.infer<typeof FormFieldOption>;

export const FormFieldValidation = z.strictObject({
  regex: z.string().max(200).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  min_length: z.number().int().min(0).optional(),
  max_length: z.number().int().min(0).optional(),
});
export type FormFieldValidation = z.infer<typeof FormFieldValidation>;

/** `map_to` ที่ประกาศได้ (09 §3) — custom_field:<key> ตรวจกับ field_definitions จริงตอน publish (ไม่ใช่ที่นี่) */
export const FormFieldMapTo = z.union([z.literal("ticket.subject"), z.literal("ticket.body"), z.literal("ticket.priority"), z.literal("ticket.cc"), z.string().regex(/^custom_field:.+$/)]);
export type FormFieldMapTo = z.infer<typeof FormFieldMapTo>;

export const FormField = z.strictObject({
  key: FormFieldKey,
  type: FormFieldType,
  label: I18n,
  help: I18n.optional(),
  required: z.boolean().optional(),
  options: z.array(FormFieldOption).optional(),
  options_from: z.literal("priority_scheme").optional(),
  validation: FormFieldValidation.optional(),
  visible_if: Condition.optional(),
  required_if: Condition.optional(),
  map_to: FormFieldMapTo.optional(),
  default: z.unknown().optional(),
  // file type — spec 09 §3 (W3: submit ปฏิเสธ 422 not_supported_yet — R3)
  max: z.number().int().positive().optional(),
  accept: z.array(z.string()).optional(),
});
export type FormField = z.infer<typeof FormField>;

export const FormPage = z.strictObject({
  title: I18n,
  fields: z.array(FormField).min(1),
});
export type FormPage = z.infer<typeof FormPage>;

export const FormRuleThen = z.strictObject({
  set_team: z.uuid().optional(),
  add_tags: z.array(z.string().min(1).max(64)).optional(),
  set_priority: z.string().optional(),
  set_field: z.strictObject({ key: z.string().min(1), value: z.unknown() }).optional(),
});
export type FormRuleThen = z.infer<typeof FormRuleThen>;

export const FormRule = z.strictObject({
  if: Condition,
  then: FormRuleThen,
});
export type FormRule = z.infer<typeof FormRule>;

function collectKeys(pages: FormPage[]): string[] {
  return pages.flatMap((p) => p.fields.map((f) => f.key));
}

/** [SA-06B] token ของ `accept` ต่อฟิลด์ file — นามสกุล ".png" หรือ mime "image/png" (จำกัดได้อย่างเดียว ไม่ขยาย allowlist) */
const ACCEPT_TOKEN = /^(\.[a-z0-9]{1,16}|[a-z0-9][a-z0-9!#$&.^_-]{0,126}\/[a-z0-9][a-z0-9!#$&.^_-]{0,126})$/;
/** [SA-06B] จำนวนไฟล์ต่อฟิลด์ (contract: 1–20 · default 1) */
export const FORM_FILE_MAX_COUNT = 20;

export const FormSchema = z
  .strictObject({
    pages: z.array(FormPage).min(1),
    rules: z.array(FormRule).default([]),
  })
  .superRefine((v, ctx) => {
    const keys = collectKeys(v.pages);
    const seen = new Set<string>();
    for (const k of keys) {
      if (seen.has(k)) {
        ctx.addIssue({ code: "custom", message: `key ซ้ำ: ${k}`, path: ["pages"] });
      }
      seen.add(k);
    }
    // [SA-06B · #274] field semantics ตอนนิยาม/เผยแพร่: file ห้าม map_to/default · max ≤ 20 · accept token รูปแบบถูก
    for (const page of v.pages) {
      for (const f of page.fields) {
        if (f.type !== "file") continue;
        if (f.map_to !== undefined) ctx.addIssue({ code: "custom", message: `ฟิลด์ file (${f.key}) ใช้ map_to ไม่ได้`, path: ["pages"] });
        if (f.default !== undefined && f.default !== "" && f.default !== null) ctx.addIssue({ code: "custom", message: `ฟิลด์ file (${f.key}) ใช้ default ไม่ได้`, path: ["pages"] });
        if (f.max !== undefined && (f.max < 1 || f.max > FORM_FILE_MAX_COUNT)) {
          ctx.addIssue({ code: "custom", message: `ฟิลด์ file (${f.key}) max ต้องอยู่ระหว่าง 1–${FORM_FILE_MAX_COUNT}`, path: ["pages"] });
        }
        for (const token of f.accept ?? []) {
          if (!ACCEPT_TOKEN.test(token)) ctx.addIssue({ code: "custom", message: `ฟิลด์ file (${f.key}) accept token ไม่ถูกต้อง: ${token}`, path: ["pages"] });
        }
      }
    }
  });
export type FormSchema = z.infer<typeof FormSchema>;

// ---------------------------------------------------------------------------
// Admin entity
// ---------------------------------------------------------------------------

export const Form = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: I18n,
  service_id: z.uuid(),
  default_team_id: z.uuid().nullable(),
  visibility: FormVisibility,
  require_otp_for_guest: z.boolean(),
  captcha: z.boolean(),
  success_message: I18n.nullable(),
  redirect_url: z.string().nullable(),
  embed_allowed_origins: z.array(z.string()),
  schema: FormSchema,
  version: z.number().int(),
  status: FormStatus,
  published_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type Form = z.infer<typeof Form>;

const FormSlug = z
  .string()
  .trim()
  .regex(/^[a-z0-9-]{2,64}$/, "ตัวพิมพ์เล็ก ตัวเลข - เท่านั้น ความยาว 2-64");

export const FormInput = z.strictObject({
  slug: FormSlug,
  name: I18n,
  service_id: z.uuid(),
  default_team_id: z.uuid().nullable().optional(),
  visibility: FormVisibility.optional(),
  require_otp_for_guest: z.boolean().optional(),
  captcha: z.boolean().optional(),
  success_message: I18n.optional(),
  redirect_url: z.string().max(500).optional(),
  embed_allowed_origins: z.array(z.string().max(200)).max(20).optional(),
  schema: FormSchema.optional(),
});
export type FormInput = z.infer<typeof FormInput>;

export const FormPatch = FormInput.omit({ slug: true }).partial();
export type FormPatch = z.infer<typeof FormPatch>;

export const FormPublishResult = Form;
export type FormPublishResult = z.infer<typeof FormPublishResult>;

// ---------------------------------------------------------------------------
// Public / submit
// ---------------------------------------------------------------------------

export const FormPublic = z.object({
  /** [Lead #76] id ของฟอร์ม — portal ใช้ส่ง `form_id` ตอนบันทึก deflection */
  id: z.uuid(),
  slug: z.string(),
  name: I18n,
  schema: FormSchema,
  version: z.number().int(),
  visibility: FormVisibility,
  require_otp_for_guest: z.boolean(),
  captcha: z.boolean(),
  success_message: I18n.nullable(),
  redirect_url: z.string().nullable(),
  /** [T-041 R8] Turnstile site key (public — ปลอดภัยที่จะโชว์) เฉพาะ form ที่ captcha=true · null เมื่อไม่ได้ตั้ง env/ปิด captcha */
  turnstile_site_key: z.string().nullable().optional(),
});
export type FormPublic = z.infer<typeof FormPublic>;

/** ต้อง confirm ก่อนขอ OTP — กัน link-preview bot (เหมือน GuestOtpRequest ของ T-024) */
export const FormOtpRequest = z.object({ email: z.email(), confirm: z.literal(true) });
export type FormOtpRequest = z.infer<typeof FormOtpRequest>;

export const FormOtpVerify = z.object({ email: z.email(), code: z.string().regex(/^\d{6}$/) });
export type FormOtpVerify = z.infer<typeof FormOtpVerify>;

export const FormOtpSession = z.object({ otp_session: z.string() });
export type FormOtpSession = z.infer<typeof FormOtpSession>;

export const FormSubmit = z.strictObject({
  payload: z.record(z.string(), z.unknown()),
  otp_session: z.string().optional(),
  turnstile_token: z.string().optional(),
  embed_token: z.string().optional(),
  /** [SA-06B · #274] ฟอร์มที่มี field file: ระบุ draft ที่ถือไฟล์ (+ header X-Form-Draft-Token) · client เก่าไม่ส่ง = เหมือนเดิม */
  version: z.number().int().positive().optional(),
  draft_id: z.uuid().optional(),
});
export type FormSubmit = z.infer<typeof FormSubmit>;

export const FormSubmitResult = z.object({
  ticket_id: z.string(), // public id
  tracking_url: z.string(),
});
export type FormSubmitResult = z.infer<typeof FormSubmitResult>;

// ---------------------------------------------------------------------------
// [SA-06B · #274] Upload drafts — capability API ของ form file fields
// docs/FORM-ATTACHMENTS-CONTRACT-2026-09-26.md §"API surface" (frozen)
// ---------------------------------------------------------------------------

/** POST /api/v1/forms/:slug/drafts — สร้าง browser draft capability (token อยู่ใน header เท่านั้น) */
export const FormDraftCreate = z.strictObject({
  version: z.number().int().positive(),
  otp_session: z.string().optional(),
  embed_token: z.string().optional(),
  turnstile_token: z.string().optional(),
});
export type FormDraftCreate = z.infer<typeof FormDraftCreate>;

export const FormDraftCreated = z.object({
  draft_id: z.uuid(),
  /** opaque 256-bit — ส่งกลับครั้งเดียวตรงนี้ · ห้ามใน URL/log/persisted payload */
  draft_token: z.string(),
  version: z.number().int(),
  expires_at: z.iso.datetime(),
  max_files: z.number().int(),
  max_total_bytes: z.number().int(),
  max_file_bytes: z.number().int(),
});
export type FormDraftCreated = z.infer<typeof FormDraftCreated>;

/** POST .../attachments/presign — idempotent ต่อ (draft, upload_key) */
export const FormDraftAttachmentPresign = z.strictObject({
  upload_key: z.uuid(),
  field_key: FormFieldKey,
  filename: z.string().min(1).max(255),
  size: z.number().int().positive(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  mime: z.string().max(255).optional(),
});
export type FormDraftAttachmentPresign = z.infer<typeof FormDraftAttachmentPresign>;

export const FormDraftReservation = z.object({
  attachment_id: z.uuid(),
  /** Worker-local path — ยังต้องมี capability/session จึงใช้ได้ */
  upload_url: z.string(),
  expires_at: z.iso.datetime(),
  status: z.literal("pending"),
});
export type FormDraftReservation = z.infer<typeof FormDraftReservation>;

export const FormDraftUploadAck = z.object({
  id: z.uuid(),
  received: z.number().int(),
  status: z.literal("pending"),
});
export type FormDraftUploadAck = z.infer<typeof FormDraftUploadAck>;

export const FormDraftAttachmentCompleteResult = z.object({
  id: z.uuid(),
  field_key: z.string(),
  filename: z.string(),
  mime: z.string(),
  size: z.number().int(),
  risk_flag: z.boolean(),
  status: z.literal("ready"),
});
export type FormDraftAttachmentCompleteResult = z.infer<typeof FormDraftAttachmentCompleteResult>;

export const FormDraftFileMeta = z.object({
  id: z.uuid(),
  field_key: z.string(),
  filename: z.string(),
  mime: z.string().nullable(),
  size: z.number().int(),
  risk_flag: z.boolean(),
  status: z.enum(["pending", "ready"]),
  upload_key: z.uuid(),
  /** เวลาที่ reservation หมด (null = ผ่าน reservation แล้ว — ใช้ได้ถึง draft expiry เมื่อ ready) */
  upload_expires_at: z.iso.datetime().nullable(),
});
export type FormDraftFileMeta = z.infer<typeof FormDraftFileMeta>;

/** GET /api/v1/forms/:slug/drafts/:draftId — state + metadata เท่านั้น (ไม่มี download URL/credential recovery) */
export const FormDraftState = z.object({
  draft_id: z.uuid(),
  version: z.number().int(),
  expires_at: z.iso.datetime(),
  submitted: z.boolean(),
  files: z.array(FormDraftFileMeta),
});
export type FormDraftState = z.infer<typeof FormDraftState>;

export const FormSubmission = z.object({
  id: z.uuid(),
  form_id: z.uuid(),
  version: z.number().int(),
  ticket_id: z.string().nullable(), // ticket public id (null = การ์ดถูกลบ)
  submitted_by: z.object({
    user_id: z.uuid().optional(),
    email: z.string(),
    kind: z.enum(["user", "guest", "embed"]),
  }),
  payload: z.record(z.string(), z.unknown()),
  created_at: z.iso.datetime(),
});
export type FormSubmission = z.infer<typeof FormSubmission>;

// ---------------------------------------------------------------------------
// Portal catalog [#211 · T-072]
// ---------------------------------------------------------------------------

/** visibility ที่ portal catalog เปิดเผยได้ — `internal` ห้ามปรากฏเป็นเด็ดขาด */
export const PortalFormVisibility = z.enum(["public", "authenticated"]);
export type PortalFormVisibility = z.infer<typeof PortalFormVisibility>;

/** รายการฟอร์มใน catalog — เฉพาะฟิลด์ที่ portal ต้องใช้เลือกฟอร์ม (ไม่มี schema/origin/id ของฟอร์ม) */
export const PortalFormSummary = z.object({
  slug: z.string(),
  name: I18n,
  service: z.object({ id: z.uuid(), code: z.string(), name: I18n }),
  visibility: PortalFormVisibility,
  require_otp_for_guest: z.boolean(),
  captcha: z.boolean(),
});
export type PortalFormSummary = z.infer<typeof PortalFormSummary>;

/** GET /api/v1/forms/portal — เรียงตาม service (code) แล้วชื่อฟอร์ม (stable) เพื่อให้ UI จัดกลุ่มง่าย */
export const PortalFormCatalog = z.object({ items: z.array(PortalFormSummary) });
export type PortalFormCatalog = z.infer<typeof PortalFormCatalog>;

// ---------------------------------------------------------------------------
// Embed
// ---------------------------------------------------------------------------

export const EmbedTokenRequest = z.strictObject({
  user: z.union([z.strictObject({ email: z.email() }), z.strictObject({ sso_sub: z.uuid() })]),
  forms: z.array(z.string().min(1)).min(1).max(50),
  ttl: z.number().int().positive().max(300).optional(),
});
export type EmbedTokenRequest = z.infer<typeof EmbedTokenRequest>;

export const EmbedToken = z.object({
  token: z.string(),
  expires_at: z.iso.datetime(),
});
export type EmbedToken = z.infer<typeof EmbedToken>;
